"use client";

import { StatusCodes } from "http-status-codes";
import { useRouter } from "next/navigation";
import { useState } from "react";

import { useModifyShoppingCartMutation } from "@/store/shoppingCartApi";

/**
 * Adds an offer at the shopper's quantity, then opens the cart acknowledgement page, as the live product page
 * does. Tracks which offer is being added and which one failed, so only that row shows it.
 */
export default function useAddSellerOptionToCart({ mpId, quantity, categoryName, onAdded }: AddToCartOptions) {
  const router = useRouter();
  const [modifyShoppingCart] = useModifyShoppingCartMutation();
  const [addingVendorProductId, setAddingVendorProductId] = useState<number | null>(null);
  const [failedVendorProductId, setFailedVendorProductId] = useState<number | null>(null);

  const addToCart = async (offer: VendorOptionState) => {
    if (addingVendorProductId !== null) {
      return;
    }
    setFailedVendorProductId(null);
    // The cart rejects an item without its leaf category.
    if (categoryName === "") {
      setFailedVendorProductId(offer.vendorProductId);
      return;
    }
    setAddingVendorProductId(offer.vendorProductId);

    try {
      const result = await modifyShoppingCart({
        productsToModify: [
          {
            mpId,
            vendorProductId: offer.vendorProductId,
            quantity,
            minimumQuantity: offer.minimumQuantityPurchasable,
            categoryName,
          },
        ],
        incrementQuantity: false,
      }).unwrap();
      if (result.status !== StatusCodes.OK) {
        throw new Error(`Add to cart returned ${result.status}`);
      }
    } catch {
      setAddingVendorProductId(null);
      setFailedVendorProductId(offer.vendorProductId);
      return;
    }

    try {
      onAdded?.(offer);
    } catch (error) {
      // The cart already has the offer, so a reporting failure must not keep the acknowledgement page from opening.
      console.error("Could not report the add to cart", error);
    }
    const queryId = new URL(window.location.href).searchParams.get("queryID");
    router.push(
      `/shopping-cart-acknowledgement/${mpId}/${offer.vendorProductId}${queryId ? `?queryID=${encodeURIComponent(queryId)}` : ""}`,
    );
  };

  return { addToCart, addingVendorProductId, failedVendorProductId };
}

interface AddToCartOptions {
  mpId: number;
  quantity: number;
  /** Leaf category name, which the cart requires. */
  categoryName: string;
  /** Runs once the cart has the offer, before the acknowledgement page opens. The shopping view reports the add here. */
  onAdded?: (offer: VendorOptionState) => void;
}
